import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Landmark, Wallet, Plus, ArrowLeftRight, ArrowDownLeft, ArrowUpRight, Download,
  Undo2, CheckCheck, AlertTriangle, Banknote, Search,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr, fmtDate, titleCase, today } from "@/lib/format";
import { useCanEdit } from "@/context/AuthContext";
import { CardList, DataCard, TableWrap } from "@/components/DataCards";
import Pagination, { usePagedList, downloadFile } from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const TABS = [
  ["accounts", "Accounts"],
  ["entries", "Transactions"],
  ["reconcile", "Reconciliation"],
  ["reports", "Reports"],
];

const ACCOUNT_TYPES = [
  ["BANK", "Bank account"],
  ["CASH", "Cash in hand"],
  ["CARD", "Credit card"],
  ["WALLET", "Wallet / UPI"],
];

const TYPE_ICON = { BANK: Landmark, CASH: Banknote, CARD: Wallet, WALLET: Wallet };

const blankAccount = () => ({
  name: "", account_type: "BANK", bank_name: "", account_number: "", ifsc: "",
  branch: "", upi_id: "", opening_balance: "0", opening_date: today(),
  is_default: false, is_active: true, notes: "",
});

const Tile = ({ icon: Icon, label, value, tone = "text-blue-500", testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-500">
      <Icon size={13} className={tone} />
      {label}
    </div>
    <div className="mt-2 font-heading text-2xl font-bold tabular-nums">{value}</div>
  </div>
);

/** Money is coloured by which way it went, everywhere on this page. */
const Amount = ({ value, direction }) => (
  <span className={`font-mono tabular-nums font-semibold ${
    direction === "IN" ? "text-emerald-700" : "text-rose-700"}`}>
    {direction === "IN" ? "+" : "−"}{inr(value)}
  </span>
);

export default function Banking() {
  const canEdit = useCanEdit();
  const [tab, setTab] = useState("accounts");

  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [parties, setParties] = useState([]);
  const [unassignedTotal, setUnassignedTotal] = useState(0);

  const loadAccounts = useCallback(async () => {
    try {
      const [a, s] = await Promise.all([
        api.get("/banking/accounts", { params: { include_inactive: true } }),
        api.get("/banking/summary"),
      ]);
      setAccounts(a.data);
      setSummary(s.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  useEffect(() => {
    api.get("/banking/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/parties").then(({ data }) => setParties(data)).catch(() => {});
    api.get("/banking/unassigned-payments", { params: { page_size: 1 } })
      .then((res) => setUnassignedTotal(Number(res.headers?.["x-total-count"] || 0)))
      .catch(() => {});
  }, []);

  const activeAccounts = useMemo(() => accounts.filter((a) => a.is_active), [accounts]);

  return (
    <div className="space-y-5" data-testid="banking-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold">Banking &amp; Cash</h1>
          <p className="text-xs text-slate-500">
            Every rupee in and out, across your bank, cash and wallet accounts.
          </p>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Tile icon={Wallet} label="Total balance" value={inr(summary.total_balance)}
            testid="bank-tile-total" />
          <Tile icon={Landmark} label="In bank" value={inr(summary.bank_balance)}
            testid="bank-tile-bank" />
          <Tile icon={Banknote} label="Cash in hand" value={inr(summary.cash_balance)}
            tone="text-emerald-500" testid="bank-tile-cash" />
          <Tile icon={CheckCheck} label="Accounts" value={summary.account_count}
            tone="text-slate-400" testid="bank-tile-count" />
        </div>
      )}

      {unassignedTotal > 0 && (
        <UnassignedBanner count={unassignedTotal} accounts={activeAccounts} canEdit={canEdit}
          onDone={() => {
            loadAccounts();
            api.get("/banking/unassigned-payments", { params: { page_size: 1 } })
              .then((res) => setUnassignedTotal(Number(res.headers?.["x-total-count"] || 0)))
              .catch(() => {});
          }} />
      )}

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map(([key, label]) => (
          <button key={key} data-testid={`bank-tab-${key}`} onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors duration-150 ${
              tab === key ? "border-blue-600 text-blue-700"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "accounts" && (
        <AccountsTab accounts={accounts} canEdit={canEdit} reload={loadAccounts} />
      )}
      {tab === "entries" && (
        <EntriesTab accounts={activeAccounts} categories={categories} parties={parties}
          canEdit={canEdit} onChanged={loadAccounts} />
      )}
      {tab === "reconcile" && (
        <ReconcileTab accounts={activeAccounts} canEdit={canEdit} />
      )}
      {tab === "reports" && <ReportsTab accounts={activeAccounts} />}
    </div>
  );
}

/* ------------------------------------------------------------------ unassigned */
function UnassignedBanner({ count, accounts, canEdit, onDone }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState({});
  const [accountId, setAccountId] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/banking/unassigned-payments", { params: { page_size: 200 } });
      setRows(data);
      setPicked(Object.fromEntries(data.map((p) => [p.id, true])));
      setAccountId(accounts.find((a) => a.is_default)?.id || accounts[0]?.id || "");
      setOpen(true);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const assign = async () => {
    const ids = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);
    if (!accountId || !ids.length) return;
    try {
      const { data } = await api.post("/banking/unassigned-payments/assign",
        { account_id: accountId, payment_ids: ids });
      toast.success(`${data.assigned} payment${data.assigned === 1 ? "" : "s"} placed`);
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3"
        data-testid="bank-unassigned-banner">
        <div className="flex items-start gap-2 text-xs text-amber-900">
          <AlertTriangle size={15} className="mt-px shrink-0" />
          <span>
            <strong>{count}</strong> payment{count === 1 ? " was" : "s were"} recorded before
            banking was switched on, so {count === 1 ? "it is" : "they are"} not in any account
            balance yet. We will not guess which account — place {count === 1 ? "it" : "them"} yourself.
          </span>
        </div>
        {canEdit && !!accounts.length && (
          <Button size="sm" variant="outline" onClick={load} data-testid="bank-place-payments">
            Place them
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="bank-assign-dialog">
          <DialogHeader>
            <DialogTitle>Place existing payments</DialogTitle>
            <DialogDescription>
              Each payment will be added to the chosen account, dated as it was recorded.
              Uncleared cheques stay out of the balance until they clear.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Account</Label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger data-testid="bank-assign-account"><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="max-h-72 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100">
              {rows.map((p) => (
                <label key={p.id} className="flex cursor-pointer items-center gap-3 px-3 py-2 text-xs hover:bg-slate-50">
                  <input type="checkbox" checked={!!picked[p.id]}
                    onChange={(e) => setPicked((s) => ({ ...s, [p.id]: e.target.checked }))} />
                  <span className="font-mono font-semibold">{p.payment_number}</span>
                  <span className="text-slate-500">{fmtDate(p.payment_date)}</span>
                  <span className="flex-1 truncate">{p.party_name}</span>
                  <Amount value={p.amount} direction={p.payment_type === "PAYMENT_IN" ? "IN" : "OUT"} />
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={assign} className="bg-blue-600 hover:bg-blue-700"
              data-testid="bank-assign-save">Place payments</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ------------------------------------------------------------------ accounts */
function AccountsTab({ accounts, canEdit, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankAccount());

  const startNew = () => { setEditing(null); setForm(blankAccount()); setOpen(true); };
  const startEdit = (a) => {
    setEditing(a);
    setForm({ ...blankAccount(), ...a, opening_balance: String(a.opening_balance ?? 0) });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Give the account a name"); return; }
    const payload = { ...form, opening_balance: Number(form.opening_balance) || 0 };
    try {
      if (editing) await api.patch(`/banking/accounts/${editing.id}`, payload);
      else await api.post("/banking/accounts", payload);
      toast.success(editing ? "Account updated" : "Account added");
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const close = async (a) => {
    if (!window.confirm(`Close ${a.name}? Its entries stay in the books.`)) return;
    try {
      await api.delete(`/banking/accounts/${a.id}`);
      toast.success("Account closed");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {canEdit && (
          <Button size="sm" onClick={startNew} className="gap-1.5 bg-blue-600 hover:bg-blue-700"
            data-testid="bank-add-account">
            <Plus size={15} /> Add account
          </Button>
        )}
      </div>

      {!accounts.length ? (
        <div className="rounded-lg border border-dashed border-slate-300 px-6 py-14 text-center"
          data-testid="bank-empty">
          <Landmark size={26} className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm font-medium text-slate-700">No accounts yet</p>
          <p className="mt-1 text-xs text-slate-500">
            Add your current account, cash box or UPI wallet with its opening balance,
            and every payment from here on will land in one of them.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {accounts.map((a) => {
            const Icon = TYPE_ICON[a.account_type] || Landmark;
            return (
              <div key={a.id} data-testid={`bank-account-${a.id}`}
                className={`rounded-lg border bg-white p-4 ${
                  a.is_active ? "border-slate-200" : "border-slate-200 opacity-60"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="grid h-8 w-8 place-items-center rounded-md bg-blue-50 text-blue-600">
                      <Icon size={15} />
                    </span>
                    <div>
                      <div className="text-sm font-semibold">{a.name}</div>
                      <div className="text-[11px] text-slate-500">
                        {titleCase(a.account_type)}
                        {a.bank_name ? ` · ${a.bank_name}` : ""}
                        {a.account_number ? ` · ••${String(a.account_number).slice(-4)}` : ""}
                      </div>
                    </div>
                  </div>
                  {a.is_default && (
                    <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                      Default
                    </span>
                  )}
                  {!a.is_active && (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
                      Closed
                    </span>
                  )}
                </div>
                <div className="mt-4 font-heading text-2xl font-bold tabular-nums">
                  {inr(a.current_balance)}
                </div>
                <div className="text-[11px] text-slate-500">
                  Opened {fmtDate(a.opening_date)} at {inr(a.opening_balance)}
                </div>
                {canEdit && (
                  <div className="-mx-1.5 mt-2 flex gap-1 [&>button]:inline-flex
                    [&>button]:min-h-[40px] [&>button]:items-center [&>button]:rounded-md
                    [&>button]:px-1.5 [&>button]:transition-colors [&>button]:duration-150
                    [&>button]:active:bg-slate-100">
                    <button className="text-xs text-blue-700 hover:text-blue-900 transition-colors duration-150"
                      onClick={() => startEdit(a)} data-testid={`bank-edit-${a.id}`}>Edit</button>
                    {a.is_active && (
                      <button className="text-xs text-slate-400 hover:text-rose-600 transition-colors duration-150"
                        onClick={() => close(a)} data-testid={`bank-close-${a.id}`}>Close</button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="bank-account-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit account" : "Add account"}</DialogTitle>
            <DialogDescription>
              The opening balance is what the account held on the opening date — everything
              recorded here builds on it.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Name</Label>
              <Input value={form.name} data-testid="bank-account-name"
                placeholder="HDFC Current A/c"
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Type</Label>
              <Select value={form.account_type}
                onValueChange={(v) => setForm((f) => ({ ...f, account_type: v }))}>
                <SelectTrigger data-testid="bank-account-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACCOUNT_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Bank</Label>
              <Input value={form.bank_name || ""}
                onChange={(e) => setForm((f) => ({ ...f, bank_name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Account number</Label>
              <Input className="font-mono" value={form.account_number || ""}
                onChange={(e) => setForm((f) => ({ ...f, account_number: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">IFSC</Label>
              <Input className="font-mono uppercase" value={form.ifsc || ""}
                onChange={(e) => setForm((f) => ({ ...f, ifsc: e.target.value.toUpperCase() }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Opening balance</Label>
              <Input className="font-mono text-right" value={form.opening_balance}
                data-testid="bank-account-opening"
                onChange={(e) => setForm((f) => ({ ...f, opening_balance: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Opening date</Label>
              <Input type="date" value={form.opening_date || ""}
                onChange={(e) => setForm((f) => ({ ...f, opening_date: e.target.value }))} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={form.notes || ""}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <Switch checked={!!form.is_default} data-testid="bank-account-default"
                onCheckedChange={(v) => setForm((f) => ({ ...f, is_default: v }))} />
              <span className="text-xs text-slate-600">Preselect this account on new payments</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700"
              data-testid="bank-account-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------------ transactions */
function EntriesTab({ accounts, categories, parties, canEdit, onChanged }) {
  const [accountId, setAccountId] = useState("ALL");
  const [direction, setDirection] = useState("ALL");
  const [q, setQ] = useState("");
  const [range, setRange] = useState({ from_date: "", to_date: "" });

  const [entryOpen, setEntryOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const query = useMemo(() => ({
    account_id: accountId === "ALL" ? undefined : accountId,
    direction: direction === "ALL" ? undefined : direction,
    q: q || undefined,
    from_date: range.from_date || undefined,
    to_date: range.to_date || undefined,
  }), [accountId, direction, q, range]);

  const { rows, total, page, setPage, size, setSize, loading, reload } =
    usePagedList("/banking/transactions", query);

  const refresh = () => { reload(); onChanged(); };

  const reverse = async (t) => {
    const what = t.transfer_group ? "this transfer (both legs)" : "this entry";
    if (!window.confirm(`Reverse ${what}? It stays visible, marked reversed.`)) return;
    try {
      await api.post(`/banking/transactions/${t.id}/reverse`);
      toast.success("Entry reversed");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[180px] flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="h-9 pl-8" placeholder="Search party, reference, notes"
            data-testid="bank-search" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={accountId} onValueChange={setAccountId}>
          <SelectTrigger className="h-9 w-full sm:w-52" data-testid="bank-account-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All accounts</SelectItem>
            {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={direction} onValueChange={setDirection}>
          <SelectTrigger className="h-9 w-full sm:w-36" data-testid="bank-direction-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">In and out</SelectItem>
            <SelectItem value="IN">Money in</SelectItem>
            <SelectItem value="OUT">Money out</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" className="h-9 w-full sm:w-36" value={range.from_date}
          onChange={(e) => setRange((r) => ({ ...r, from_date: e.target.value }))} />
        <Input type="date" className="h-9 w-full sm:w-36" value={range.to_date}
          onChange={(e) => setRange((r) => ({ ...r, to_date: e.target.value }))} />
        {canEdit && !!accounts.length && (
          <>
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="bank-add-entry"
              onClick={() => setEntryOpen(true)}>
              <Plus size={15} /> Entry
            </Button>
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="bank-add-transfer"
              onClick={() => setTransferOpen(true)}>
              <ArrowLeftRight size={15} /> Transfer
            </Button>
          </>
        )}
      </div>

      <TableWrap>
        <table className="w-full text-sm" data-testid="bank-transactions-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Date</th>
              <th className="px-3 py-2.5 text-left font-semibold">Particulars</th>
              <th className="px-3 py-2.5 text-left font-semibold">Account</th>
              <th className="px-3 py-2.5 text-left font-semibold">Type</th>
              <th className="px-3 py-2.5 text-left font-semibold">Reference</th>
              <th className="px-3 py-2.5 text-right font-semibold">Amount</th>
              <th className="px-4 py-2.5 text-right font-semibold"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((t) => (
              <tr key={t.id} className="hover:bg-slate-50 transition-colors duration-150"
                data-testid={`bank-txn-${t.id}`}>
                <td className="px-4 py-2.5 text-xs text-slate-600 whitespace-nowrap">{fmtDate(t.txn_date)}</td>
                <td className="px-3 py-2.5">
                  {t.reference_label || t.party_name || t.category || t.source_label}
                  {t.notes && <div className="text-[11px] text-slate-500">{t.notes}</div>}
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-600">{t.account_name}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500">{t.source_label}</td>
                <td className="px-3 py-2.5 font-mono text-[11px] text-slate-500">{t.reference_number || "—"}</td>
                <td className="px-3 py-2.5 text-right"><Amount value={t.amount} direction={t.direction} /></td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  {t.reconciled && (
                    <span title="Reconciled" className="mr-2 inline-block align-middle text-emerald-600">
                      <CheckCheck size={14} />
                    </span>
                  )}
                  {canEdit && !["RECEIPT", "PAYMENT"].includes(t.source) && (
                    <button className="text-slate-400 hover:text-rose-600 transition-colors duration-150"
                      title="Reverse entry" data-testid={`bank-reverse-${t.id}`} onClick={() => reverse(t)}>
                      <Undo2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && !loading && (
              <tr><td colSpan={7} className="px-4 py-14 text-center text-sm text-slate-500">
                No entries for these filters
              </td></tr>
            )}
          </tbody>
        </table>
      </TableWrap>

      <CardList>
        {rows.map((t) => (
          <DataCard key={t.id} testid={`bank-txn-card-${t.id}`}
            title={t.reference_label || t.party_name || t.category || t.source_label}
            subtitle={`${fmtDate(t.txn_date)} · ${t.account_name}`}
            right={<Amount value={t.amount} direction={t.direction} />}
            rows={[
              ["Type", t.source_label],
              ["Reference", t.reference_number || "—"],
              ["Reconciled", t.reconciled ? "Yes" : "No"],
            ]} />
        ))}
      </CardList>

      <Pagination total={total} page={page} setPage={setPage} size={size} setSize={setSize}
        loading={loading} testid="bank-pagination" />

      <EntryDialog open={entryOpen} setOpen={setEntryOpen} accounts={accounts}
        categories={categories} parties={parties} onSaved={refresh} />
      <TransferDialog open={transferOpen} setOpen={setTransferOpen} accounts={accounts}
        onSaved={refresh} />
    </div>
  );
}

function EntryDialog({ open, setOpen, accounts, categories, parties, onSaved }) {
  const [form, setForm] = useState({
    account_id: "", direction: "OUT", amount: "", txn_date: today(),
    category: "", party_id: "", reference_number: "", notes: "",
  });

  useEffect(() => {
    if (!open) return;
    const preferred = accounts.find((a) => a.is_default) || accounts[0];
    setForm((f) => ({ ...f, account_id: preferred?.id || "", amount: "", notes: "" }));
  }, [open, accounts]);

  const save = async () => {
    if (!form.account_id) { toast.error("Pick an account"); return; }
    if (!(Number(form.amount) > 0)) { toast.error("Enter an amount"); return; }
    try {
      await api.post("/banking/transactions", {
        ...form,
        party_id: form.party_id || undefined,
        category: form.category || undefined,
        amount: Number(form.amount),
      });
      toast.success("Entry recorded");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="bank-entry-dialog">
        <DialogHeader>
          <DialogTitle>Record money in or out</DialogTitle>
          <DialogDescription>
            For anything that is not an invoice or a bill — rent, salaries, bank charges,
            interest, capital brought in.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Direction</Label>
            <Select value={form.direction}
              onValueChange={(v) => setForm((f) => ({ ...f, direction: v }))}>
              <SelectTrigger data-testid="bank-entry-direction"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="OUT">Money out (payment / expense)</SelectItem>
                <SelectItem value="IN">Money in (deposit / income)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Account</Label>
            <Select value={form.account_id}
              onValueChange={(v) => setForm((f) => ({ ...f, account_id: v }))}>
              <SelectTrigger data-testid="bank-entry-account"><SelectValue placeholder="Select account" /></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Date</Label>
            <Input type="date" value={form.txn_date}
              onChange={(e) => setForm((f) => ({ ...f, txn_date: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Amount</Label>
            <Input className="font-mono text-right" value={form.amount} data-testid="bank-entry-amount"
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Category</Label>
            <Select value={form.category || "NONE"}
              onValueChange={(v) => setForm((f) => ({ ...f, category: v === "NONE" ? "" : v }))}>
              <SelectTrigger data-testid="bank-entry-category"><SelectValue placeholder="Uncategorised" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">Uncategorised</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Party (optional)</Label>
            <Select value={form.party_id || "NONE"}
              onValueChange={(v) => setForm((f) => ({ ...f, party_id: v === "NONE" ? "" : v }))}>
              <SelectTrigger data-testid="bank-entry-party"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">None</SelectItem>
                {parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.display_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs">Reference</Label>
            <Input className="font-mono" value={form.reference_number}
              onChange={(e) => setForm((f) => ({ ...f, reference_number: e.target.value }))} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} className="bg-blue-600 hover:bg-blue-700"
            data-testid="bank-entry-save">Save entry</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog({ open, setOpen, accounts, onSaved }) {
  const [form, setForm] = useState({
    from_account_id: "", to_account_id: "", amount: "", txn_date: today(),
    reference_number: "", notes: "",
  });

  useEffect(() => {
    if (!open) return;
    setForm((f) => ({ ...f, from_account_id: accounts[0]?.id || "",
      to_account_id: accounts[1]?.id || "", amount: "" }));
  }, [open, accounts]);

  const save = async () => {
    if (form.from_account_id === form.to_account_id) {
      toast.error("Pick two different accounts");
      return;
    }
    if (!(Number(form.amount) > 0)) { toast.error("Enter an amount"); return; }
    try {
      await api.post("/banking/transfers", { ...form, amount: Number(form.amount) });
      toast.success("Transfer recorded");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="bank-transfer-dialog">
        <DialogHeader>
          <DialogTitle>Transfer between accounts</DialogTitle>
          <DialogDescription>
            Recorded on both statements, so neither balance drifts. Cash deposited into
            the bank belongs here.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs">From</Label>
            <Select value={form.from_account_id}
              onValueChange={(v) => setForm((f) => ({ ...f, from_account_id: v }))}>
              <SelectTrigger data-testid="bank-transfer-from"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name} · {inr(a.current_balance)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">To</Label>
            <Select value={form.to_account_id}
              onValueChange={(v) => setForm((f) => ({ ...f, to_account_id: v }))}>
              <SelectTrigger data-testid="bank-transfer-to"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name} · {inr(a.current_balance)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Date</Label>
            <Input type="date" value={form.txn_date}
              onChange={(e) => setForm((f) => ({ ...f, txn_date: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Amount</Label>
            <Input className="font-mono text-right" value={form.amount} data-testid="bank-transfer-amount"
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs">Reference</Label>
            <Input className="font-mono" value={form.reference_number}
              onChange={(e) => setForm((f) => ({ ...f, reference_number: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} className="bg-blue-600 hover:bg-blue-700"
            data-testid="bank-transfer-save">Transfer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ reconciliation */
function ReconcileTab({ accounts, canEdit }) {
  const [accountId, setAccountId] = useState("");
  const [asOf, setAsOf] = useState(today());
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!accountId && accounts.length) setAccountId(accounts[0].id);
  }, [accounts, accountId]);

  const load = useCallback(async () => {
    if (!accountId) return;
    try {
      const [s, t] = await Promise.all([
        api.get(`/banking/accounts/${accountId}/reconciliation`, { params: { as_of: asOf } }),
        api.get("/banking/transactions", {
          params: { account_id: accountId, reconciled: "false", to_date: asOf, page_size: 100 },
        }),
      ]);
      setStats(s.data);
      setRows(t.data);
      setPicked({});
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [accountId, asOf]);

  useEffect(() => { load(); }, [load]);

  const pickedIds = useMemo(
    () => Object.entries(picked).filter(([, v]) => v).map(([k]) => k), [picked]);

  const pickedNet = useMemo(
    () => rows.filter((r) => picked[r.id])
      .reduce((s, r) => s + (r.direction === "IN" ? r.amount : -r.amount), 0),
    [rows, picked]);

  const mark = async () => {
    if (!pickedIds.length) return;
    setBusy(true);
    try {
      await api.post("/banking/reconcile",
        { transaction_ids: pickedIds, reconciled: true, statement_date: asOf });
      toast.success(`${pickedIds.length} entries reconciled`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  if (!accounts.length) {
    return <div className="rounded-lg border border-dashed border-slate-300 px-6 py-12 text-center text-sm text-slate-500">
      Add an account first.
    </div>;
  }

  return (
    <div className="space-y-4" data-testid="bank-reconcile">
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1.5">
          <Label className="text-xs">Account</Label>
          <Select value={accountId} onValueChange={setAccountId}>
            <SelectTrigger className="h-9 w-56" data-testid="reconcile-account"><SelectValue /></SelectTrigger>
            <SelectContent>
              {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Statement date</Label>
          <Input type="date" className="h-9 w-40" value={asOf}
            data-testid="reconcile-date" onChange={(e) => setAsOf(e.target.value)} />
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Tile icon={Wallet} label="Balance in books" value={inr(stats.book_balance)}
            testid="reconcile-book" />
          <Tile icon={CheckCheck} label="Reconciled balance" value={inr(stats.reconciled_balance)}
            tone="text-emerald-500" testid="reconcile-confirmed" />
          <Tile icon={ArrowDownLeft} label="Unconfirmed in" value={inr(stats.unreconciled_in)}
            tone="text-emerald-500" testid="reconcile-unrec-in" />
          <Tile icon={ArrowUpRight} label="Unconfirmed out" value={inr(stats.unreconciled_out)}
            tone="text-rose-500" testid="reconcile-unrec-out" />
        </div>
      )}

      {stats && (
        <p className="text-xs text-slate-500">
          Tick everything your bank statement shows as of {fmtDate(asOf)}. When the confirmed
          figure matches the closing balance on that statement, the account is reconciled —
          {" "}{stats.unreconciled_count} entr{stats.unreconciled_count === 1 ? "y is" : "ies are"} still open.
        </p>
      )}

      <TableWrap>
        <table className="w-full text-sm" data-testid="reconcile-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold w-10"></th>
              <th className="px-3 py-2.5 text-left font-semibold">Date</th>
              <th className="px-3 py-2.5 text-left font-semibold">Particulars</th>
              <th className="px-3 py-2.5 text-left font-semibold">Reference</th>
              <th className="px-4 py-2.5 text-right font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((t) => (
              <tr key={t.id} className="hover:bg-slate-50 transition-colors duration-150">
                <td className="px-4 py-2.5">
                  <input type="checkbox" disabled={!canEdit} checked={!!picked[t.id]}
                    data-testid={`reconcile-check-${t.id}`}
                    onChange={(e) => setPicked((s) => ({ ...s, [t.id]: e.target.checked }))} />
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">{fmtDate(t.txn_date)}</td>
                <td className="px-3 py-2.5">
                  {t.reference_label || t.party_name || t.category || t.source_label}
                </td>
                <td className="px-3 py-2.5 font-mono text-[11px] text-slate-500">{t.reference_number || "—"}</td>
                <td className="px-4 py-2.5 text-right"><Amount value={t.amount} direction={t.direction} /></td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={5} className="px-4 py-14 text-center text-sm text-slate-500">
                Nothing left to confirm on this account
              </td></tr>
            )}
          </tbody>
        </table>
      </TableWrap>

      {canEdit && !!rows.length && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-xs text-slate-600">
            {pickedIds.length} selected · net{" "}
            <span className="font-mono font-semibold tabular-nums">{inr(pickedNet)}</span>
          </div>
          <Button size="sm" disabled={!pickedIds.length || busy} onClick={mark}
            className="gap-1.5 bg-blue-600 hover:bg-blue-700" data-testid="reconcile-mark">
            <CheckCheck size={15} /> Mark reconciled
          </Button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ reports */
function ReportsTab({ accounts }) {
  const [accountId, setAccountId] = useState("");
  const [range, setRange] = useState({ from_date: "", to_date: "" });
  const [flow, setFlow] = useState(null);

  useEffect(() => {
    if (!accountId && accounts.length) setAccountId(accounts[0].id);
  }, [accounts, accountId]);

  const loadFlow = useCallback(async () => {
    try {
      const { data } = await api.get("/banking/cash-flow", {
        params: { from_date: range.from_date || undefined, to_date: range.to_date || undefined },
      });
      setFlow(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [range]);

  useEffect(() => { loadFlow(); }, [loadFlow]);

  const grab = async (path, name) => {
    try {
      await downloadFile(path, {
        format: name.endsWith("xlsx") ? "xlsx" : "pdf",
        from_date: range.from_date || undefined,
        to_date: range.to_date || undefined,
      }, name);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="bank-reports">
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1.5">
          <Label className="text-xs">From</Label>
          <Input type="date" className="h-9 w-40" value={range.from_date}
            onChange={(e) => setRange((r) => ({ ...r, from_date: e.target.value }))} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">To</Label>
          <Input type="date" className="h-9 w-40" value={range.to_date}
            onChange={(e) => setRange((r) => ({ ...r, to_date: e.target.value }))} />
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold">Bank book</div>
            <div className="text-xs text-slate-500">
              Every entry on one account with a running balance — what a CA reconciles against.
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger className="h-9 w-52" data-testid="bankbook-account"><SelectValue /></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="bankbook-pdf"
              disabled={!accountId}
              onClick={() => grab(`/banking/accounts/${accountId}/statement/export`, "bank-book.pdf")}>
              <Download size={14} /> PDF
            </Button>
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="bankbook-excel"
              disabled={!accountId}
              onClick={() => grab(`/banking/accounts/${accountId}/statement/export`, "bank-book.xlsx")}>
              <Download size={14} /> Excel
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <div className="text-sm font-semibold">Cash flow summary</div>
            <div className="text-xs text-slate-500">
            Where the money came from and where it went. Transfers between your own
            accounts are left out — they are not money entering or leaving the business.
          </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="cashflow-pdf"
              onClick={() => grab("/banking/cash-flow/export", "cash-flow.pdf")}>
              <Download size={14} /> PDF
            </Button>
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="cashflow-excel"
              onClick={() => grab("/banking/cash-flow/export", "cash-flow.xlsx")}>
              <Download size={14} /> Excel
            </Button>
          </div>
        </div>
        {flow && (
          <div className="grid gap-0 sm:grid-cols-2 sm:divide-x divide-slate-200">
            <FlowList title="Money in" rows={flow.inflow} total={flow.total_in} direction="IN" />
            <FlowList title="Money out" rows={flow.outflow} total={flow.total_out} direction="OUT" />
          </div>
        )}
        {flow && (
          <div className="border-t border-slate-200 px-4 py-3 text-sm">
            Net movement{" "}
            <span className={`font-mono font-semibold tabular-nums ${
              flow.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
              {inr(flow.net)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function FlowList({ title, rows, total, direction }) {
  return (
    <div className="p-4">
      <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-slate-500">
        <span className="flex items-center gap-1.5">
          {direction === "IN" ? <ArrowDownLeft size={13} className="text-emerald-500" />
            : <ArrowUpRight size={13} className="text-rose-500" />}
          {title}
        </span>
        <span className="font-mono font-semibold tabular-nums text-slate-700">{inr(total)}</span>
      </div>
      <div className="mt-3 space-y-1.5">
        {rows.map((r) => (
          <div key={r.category} className="flex items-center justify-between gap-3 text-xs">
            <span className="truncate text-slate-600">{r.category}</span>
            <span className="font-mono tabular-nums">{inr(r.amount)}</span>
          </div>
        ))}
        {!rows.length && <div className="text-xs text-slate-400">Nothing in this period</div>}
      </div>
    </div>
  );
}
